/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { uploadMeme } from "../utils/storage";
import { supabase } from "../utils/supabase";
import { usePlausible } from "../hooks/usePlausible";
import {
  TextElement,
  StickerElement,
  CaptionElement,
  ToolType,
} from "../types";
import MemeCanvas from "../components/MemeCanvas";
import MemeSidebar from "../components/MemeSidebar";

const MemeEditor = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { trackTextAdded, trackStickerAdded, trackMemeSubmitted } =
    usePlausible();

  const templateId = searchParams.get("template");
  const importedMemeId = searchParams.get("imported_meme");
  const creatorName = searchParams.get("creator");

  const [templateImage, setTemplateImage] = useState<HTMLImageElement | null>(
    null
  );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const stageSize = { width: 600, height: 450 };
  const [tool, setTool] = useState<ToolType>("none");

  // Editor state
  const [texts, setTexts] = useState<TextElement[]>([]);
  const [stickers, setStickers] = useState<StickerElement[]>([]);
  const [captions, setCaptions] = useState<CaptionElement[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);

  // Text editing
  const [newText, setNewText] = useState("");
  const [newCaption, setNewCaption] = useState("");
  const [saving, setSaving] = useState(false);
  const [getCanvas, setGetCanvas] = useState<(() => HTMLCanvasElement) | null>(
    null
  );

  const handleCanvasReady = useCallback((getter: () => HTMLCanvasElement) => {
    setGetCanvas(() => getter);
  }, []);

  useEffect(() => {
    if (templateId) {
      loadTemplateImage(templateId);
    } else if (importedMemeId) {
      loadImportedMemeImage(importedMemeId);
    }
  }, [templateId, importedMemeId]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadTemplateImage = async (id: string) => {
    try {
      // Fetch the template from Supabase
      const { data: template, error } = await supabase
        .from("meme_templates")
        .select("image_url")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching template:", error);
        return;
      }

      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setTemplateImage(img);
      img.src = template.image_url;
    } catch (error) {
      console.error("Error loading template:", error);
    }
  };

  const loadImportedMemeImage = async (id: string) => {
    try {
      // Fetch the imported meme from Supabase
      const { data: meme, error } = await supabase
        .from("imported_memes")
        .select("url_path")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching imported meme:", error);
        return;
      }

      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setTemplateImage(img);
      img.src = meme.url_path;
    } catch (error) {
      console.error("Error loading imported meme:", error);
    }
  };

  const addText = () => {
    const newTextElement: TextElement = {
      id: `text_${Date.now()}`,
      text: newText,
      x: 0.33, // Relative position (1/3 from left)
      y: 0.33, // Relative position (1/3 from top)
      fontSize: 0.1, // Relative size (10% of canvas height)
      fontFamily: "Arial",
      fill: "#FFFFFF",
      stroke: "#000000",
      strokeWidth: 0.004, // Relative stroke width
      dir: "rtl",
      width: 0.5, // Default width (50% of canvas width) for text wrapping
      align: "center", // Default alignment
    };
    setTexts([...texts, newTextElement]);
    setNewText(""); // Clear the input
    setTool("none"); // Deselect tool after adding

    // Track text added event
    trackTextAdded(newText.length);
  };

  const addCaption = () => {
    const newCaptionElement: CaptionElement = {
      id: `caption_${Date.now()}`,
      text: newCaption,
      x: 0.5, // Center horizontally (50% from left)
      y: 0.5, // Center vertically in caption area (50% from top)
      fontSize: 0.08, // Relative size (8% of canvas height)
      fontFamily: "Arial",
      fill: "#000000",
      stroke: "#FFFFFF",
      strokeWidth: 0.002, // Relative stroke width
      dir: "rtl",
      height: 0.15, // Relative height (27% of canvas height)
      width: 0.8, // Relative width (80% of canvas width) for text wrapping
      align: "center", // Default alignment
    };
    setCaptions([...captions, newCaptionElement]);
    setNewCaption(""); // Clear the input
    setTool("none"); // Deselect tool after adding

    // Track text added event (captions are also text)
    trackTextAdded(newCaption.length);
  };

  const addSticker = (stickerUrl: string, stickerName: string) => {
    // Load the image to get its dimensions
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Calculate dimensions that maintain aspect ratio
      const imageAspectRatio = img.width / img.height;
      const baseSize = 0.17; // Base size relative to canvas

      let width, height;
      if (imageAspectRatio > 1) {
        // Image is wider than tall - fit to width
        width = baseSize;
        height = baseSize / imageAspectRatio;
      } else {
        // Image is taller than wide - fit to height
        width = baseSize * imageAspectRatio;
        height = baseSize;
      }

      const newSticker: StickerElement = {
        id: `sticker_${Date.now()}`,
        image_url: stickerUrl,
        name: stickerName,
        x: 0.5, // Center horizontally (50% from left)
        y: 0.5, // Center vertically (50% from top)
        width: width, // Calculated to maintain aspect ratio
        height: height, // Calculated to maintain aspect ratio
        rotation: 0,
        flipX: false,
        flipY: false,
      };
      setStickers([...stickers, newSticker]);
      setTool("none"); // Deselect tool after adding

      // Track sticker added event
      trackStickerAdded(stickerName);
    };
    img.onerror = () => {
      console.error("Failed to load sticker image:", stickerUrl);
      // Fallback to default square dimensions
      const newSticker: StickerElement = {
        id: `sticker_${Date.now()}`,
        image_url: stickerUrl,
        name: stickerName,
        x: 0.5,
        y: 0.5,
        width: 0.17,
        height: 0.17,
        rotation: 0,
        flipX: false,
        flipY: false,
      };
      setStickers([...stickers, newSticker]);
      setTool("none");

      // Track sticker added event (fallback case)
      trackStickerAdded(stickerName);
    };
    img.src = stickerUrl;
  };

  const handleMouseDown = (e: any) => {
    // Check if clicking on empty area
    if (e.target === e.target.getStage()) {
      setSelectedEntity(null);
      return;
    }
  };

  const handleMouseMove = (e: any) => {
    // No drawing functionality
  };

  const handleMouseUp = () => {
    // No drawing functionality
  };

  const updateText = (id: string, updates: Partial<TextElement>) => {
    setTexts(texts.map((t) => (t.id === id ? { ...t, ...updates } : t)));
  };

  const updateCaption = (id: string, updates: Partial<CaptionElement>) => {
    setCaptions(captions.map((c) => (c.id === id ? { ...c, ...updates } : c)));
  };

  const updateSticker = (id: string, updates: Partial<StickerElement>) => {
    setStickers(stickers.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  const deleteEntity = (id: string) => {
    setTexts(texts.filter((t) => t.id !== id));
    setStickers(stickers.filter((s) => s.id !== id));
    setCaptions(captions.filter((c) => c.id !== id));
    if (selectedEntity === id) {
      setSelectedEntity(null);
    }
  };

  const saveMeme = async () => {
    if (!templateId && !importedMemeId) return;
    if (!getCanvas) {
      alert("Canvas not ready. Please try again.");
      return;
    }

    setSaving(true);
    try {
      // Deselect everything to avoid having controls in the saved image
      setSelectedEntity(null);

      // Wait a brief moment for the UI to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      const canvas = getCanvas();
      if (!canvas) {
        throw new Error("Failed to get canvas");
      }
      const imageUrl = await uploadMeme(
        canvas,
        templateId || importedMemeId || "unknown"
      );

      const submissionData: any = {
        image_url: imageUrl,
        creator_name: creatorName,
        metadata: {
          texts,
          stickers,
          captions,
        },
      };

      // Set the appropriate ID field based on the source
      if (templateId) {
        submissionData.template_id = templateId;
      } else if (importedMemeId) {
        submissionData.imported_meme_id = importedMemeId;
      }

      const { error } = await supabase
        .from("meme_submissions")
        .insert(submissionData);

      if (error) throw error;

      // Track meme submitted event
      const totalElements = texts.length + stickers.length + captions.length;
      const hasText = texts.length > 0 || captions.length > 0;
      const hasStickers = stickers.length > 0;
      trackMemeSubmitted(totalElements, hasText, hasStickers);

      navigate("/done");
    } catch (error) {
      console.error("Error saving meme:", error);
      alert("Failed to save meme. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const selectedText = texts.find((t) => t.id === selectedEntity);
  const selectedCaption = captions.find((c) => c.id === selectedEntity);
  const selectedSticker = stickers.find((s) => s.id === selectedEntity);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Mobile Layout - Stack vertically */}
      <div className="flex flex-col lg:hidden gap-6">
        {/* Canvas Area - Mobile */}
        <div className="order-1">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-pink-100">
            <MemeCanvas
              templateImage={templateImage}
              texts={texts}
              stickers={stickers}
              captions={captions}
              selectedEntity={selectedEntity}
              creatorName={creatorName}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTextUpdate={updateText}
              onStickerUpdate={updateSticker}
              onCaptionUpdate={updateCaption}
              onEntitySelect={setSelectedEntity}
              onCanvasReady={handleCanvasReady}
              isMobile={true}
            />
          </div>
        </div>

        {/* Sidebar - Mobile */}
        <div className="order-2">
          <MemeSidebar
            tool={tool}
            onToolChange={setTool}
            newText={newText}
            onTextChange={setNewText}
            onAddText={addText}
            newCaption={newCaption}
            onCaptionChange={setNewCaption}
            onAddCaption={addCaption}
            onAddSticker={addSticker}
            texts={texts}
            stickers={stickers}
            captions={captions}
            selectedEntity={selectedEntity}
            selectedText={selectedText}
            selectedCaption={selectedCaption}
            selectedSticker={selectedSticker}
            saving={saving}
            onEntitySelect={setSelectedEntity}
            onDeleteEntity={deleteEntity}
            onTextUpdate={updateText}
            onCaptionUpdate={updateCaption}
            onStickerUpdate={updateSticker}
            onSaveMeme={saveMeme}
            onDeselectEntity={() => setSelectedEntity(null)}
            isMobile={true}
          />
        </div>
      </div>

      {/* Desktop Layout - Side by side */}
      <div className="hidden lg:grid grid-cols-5 gap-6">
        {/* Canvas Area - Desktop */}
        <div className="col-span-3">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-pink-100">
            <MemeCanvas
              templateImage={templateImage}
              texts={texts}
              stickers={stickers}
              captions={captions}
              selectedEntity={selectedEntity}
              creatorName={creatorName}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTextUpdate={updateText}
              onStickerUpdate={updateSticker}
              onCaptionUpdate={updateCaption}
              onEntitySelect={setSelectedEntity}
              onCanvasReady={handleCanvasReady}
              isMobile={false}
            />
          </div>
        </div>

        {/* Sidebar - Desktop */}
        <div className="col-span-2">
          <MemeSidebar
            tool={tool}
            onToolChange={setTool}
            newText={newText}
            onTextChange={setNewText}
            onAddText={addText}
            newCaption={newCaption}
            onCaptionChange={setNewCaption}
            onAddCaption={addCaption}
            onAddSticker={addSticker}
            texts={texts}
            stickers={stickers}
            captions={captions}
            selectedEntity={selectedEntity}
            selectedText={selectedText}
            selectedCaption={selectedCaption}
            selectedSticker={selectedSticker}
            saving={saving}
            onEntitySelect={setSelectedEntity}
            onDeleteEntity={deleteEntity}
            onTextUpdate={updateText}
            onCaptionUpdate={updateCaption}
            onStickerUpdate={updateSticker}
            onSaveMeme={saveMeme}
            onDeselectEntity={() => setSelectedEntity(null)}
            isMobile={false}
          />
        </div>
      </div>
    </div>
  );
};

export default MemeEditor;
